/**
 * Sentry Error Tracking Configuration for FirstCut Frontend
 * 
 * This module initializes Sentry SDK for error tracking and performance monitoring.
 * MUST be imported BEFORE any other imports in index.js
 */

import * as Sentry from "@sentry/react";

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const APP_ENV = process.env.REACT_APP_ENV || 'production';
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';
const isDevelopment = APP_ENV === 'development';

// Only initialize if DSN is configured
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    
    // Environment configuration
    environment: APP_ENV,
    release: `firstcut@${APP_VERSION}`,
    
    // Integrations
    integrations: [
      // Browser tracing for performance
      Sentry.browserTracingIntegration({
        // Track navigation
        enableInp: true,
      }),
      // Session replay for debugging (only in production)
      ...(isDevelopment ? [] : [
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ]),
    ],
    
    // Performance monitoring - 10% in production, 100% in dev
    tracesSampleRate: isDevelopment ? 1.0 : 0.1,
    
    // Session replay sample rate
    replaysSessionSampleRate: isDevelopment ? 0 : 0.1,
    replaysOnErrorSampleRate: 1.0, // Always capture replays on error
    
    // Capture PII only in development
    sendDefaultPii: isDevelopment,
    
    // Ignore common browser errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      // ResizeObserver (common browser noise)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors that are expected
      'Network Error',
      'Failed to fetch',
      // User cancellation
      'AbortError',
    ],
    
    // Filter events before sending
    beforeSend(event, hint) {
      // Don't send events in development unless explicitly testing
      if (isDevelopment && !window.__SENTRY_TEST__) {
        console.log('[Sentry Dev] Would send:', event);
        return null;
      }
      
      // Remove sensitive data from URLs
      if (event.request?.url) {
        event.request.url = event.request.url
          .replace(/session_token=[^&]+/g, 'session_token=[REDACTED]')
          .replace(/token=[^&]+/g, 'token=[REDACTED]');
      }
      
      return event;
    },
    
    // Filter transactions before sending
    beforeSendTransaction(event) {
      // Don't trace health checks
      if (event.transaction?.includes('/health') || event.transaction?.includes('/ping')) {
        return null;
      }
      return event;
    },
    
    // Debug mode in development
    debug: isDevelopment,
  });
  
  console.log(`[Sentry] Initialized for ${APP_ENV} environment`);
} else {
  console.log('[Sentry] DSN not configured - error tracking disabled');
}

/**
 * Set user context for Sentry
 * Call this after user authentication
 */
export const setSentryUser = (user) => {
  if (!SENTRY_DSN) return;
  
  Sentry.setUser({
    id: user.id || user.user_id,
    email: user.email,
    subscription_tier: user.subscription_tier,
  });
};

/**
 * Clear user context (on logout)
 */
export const clearSentryUser = () => {
  if (!SENTRY_DSN) return;
  Sentry.setUser(null);
};

/**
 * Capture an exception manually
 */
export const captureException = (error, context = {}) => {
  if (!SENTRY_DSN) {
    console.error('[Sentry] Would capture:', error, context);
    return;
  }
  Sentry.captureException(error, { extra: context });
};

/**
 * Capture a message manually
 */
export const captureMessage = (message, level = 'info') => {
  if (!SENTRY_DSN) {
    console.log(`[Sentry] Would capture message (${level}):`, message);
    return;
  }
  Sentry.captureMessage(message, level);
};

/**
 * Add a breadcrumb for debugging context
 */
export const addBreadcrumb = (message, category = 'app', data = {}) => {
  if (!SENTRY_DSN) return;
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
};

// Export Sentry for advanced usage
export { Sentry };
